import * as tslib_1 from "tslib";
var halfDivisor = 2;
var Util = /** @class */ (function () {
    function Util() {
    }
    /**
     * Determine if a string is null, undefined, empty string, or a string containing only whitespace
     */
    Util.prototype.isEmptyUnsetOrWhitespace = function (subject) {
        return subject === undefined || subject === null || subject.toString().trim().length === 0;
    };
    /**
     * Get a promise that resolves after the specified milliseconds
     */
    Util.prototype.sleep = function (milliseconds) {
        return new Promise(function (r) { return setTimeout(r, milliseconds); });
    };
    /**
     * Runs setTimeout, and returns a disconnect function for easy cancelability.
     */
    Util.prototype.timeout = function (callback, milliseconds) {
        var callbackHandle = setTimeout(function () {
            callback();
        }, milliseconds);
        return function () {
            clearTimeout(callbackHandle);
        };
    };
    /**
     * Given a string with snake-case-text in it, return camelCaseText.
     */
    Util.prototype.snakeToCamelCase = function (snakeCaseText) {
        var parts = snakeCaseText.split('-');
        var result = parts[0];
        for (var i = 1; i < parts.length; i++) {
            var part = parts[i];
            result += part[0].toUpperCase() + part.substring(1);
        }
        return result;
    };
    /**
     * Run a callback every `frequencyMilliseconds` until `totalMilliseconds` have passed
     * until any value other than undefined is returned from callback
     * @param callback - function to call. Can return a promise, which will be waited on
     */
    Util.prototype.retryUntilValue = function (callback, totalMilliseconds, frequencyMilliseconds) {
        if (frequencyMilliseconds === void 0) { frequencyMilliseconds = 15; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var end, result;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        end = Date.now() + totalMilliseconds;
                        _a.label = 1;
                    case 1:
                        if (!(Date.now() < end)) return [3 /*break*/, 4];
                        return [4 /*yield*/, Promise.resolve(callback())];
                    case 2:
                        result = _a.sent();
                        if (result !== undefined) {
                            return [2 /*return*/, result];
                        }
                        return [4 /*yield*/, this.sleep(frequencyMilliseconds)];
                    case 3:
                        _a.sent();
                        return [3 /*break*/, 1];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Run a task, and if it takes longer than `totalMilliseconds`, reject the promise.
     * If successful, return the returnvalue from the callback
     */
    Util.prototype.runOrTimeout = function (callback, totalMilliseconds) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var isTimedOut = false;
            // set a timeout. if it expires before the return has happened, then fail
            var unsetTimeout = _this.timeout(function () {
                isTimedOut = true;
                reject(new Error("Task took longer than the max " + totalMilliseconds + "ms allowed"));
            }, totalMilliseconds);
            // wait for the results
            Promise.resolve(callback()).then(function (result) {
                // do nothing, the timeout already took care of failing
                if (isTimedOut) {
                    return;
                }
                else {
                    resolve(result);
                }
            }, function (e) {
                reject(e);
                unsetTimeout();
            });
        });
    };
    /**
     * Looks for the closet ancestor, taking in to account shadow roots.
     */
    Util.prototype.getClosest = function (el, tagName) {
        var currentEl;
        while (!currentEl || currentEl.nodeName !== tagName.toUpperCase()) {
            currentEl = el.closest(tagName);
            if (!currentEl) {
                currentEl = el.getRootNode().host;
                if (currentEl) {
                    el = currentEl;
                }
                else {
                    break;
                }
            }
        }
        return currentEl;
    };
    /**
     * Load a script into the document head if it isn't loaded yet
     * @param scriptUrl - url of the script
     * @param scriptId - a unique id for the script
     * @param loadedCheck - function to call to check if the script is loaded yet. Can be a promise.
     * @param loadWaitMilliseconds - how long to wait for the script to load. Default: 15 seconds.
     */
    Util.prototype.loadScript = function (scriptUrl, scriptId, loadedCheck, loadWaitMilliseconds) {
        if (loadWaitMilliseconds === void 0) { loadWaitMilliseconds = 15000; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var initiallyLoaded, script;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, loadedCheck()];
                    case 1:
                        initiallyLoaded = _a.sent();
                        if (!document.getElementById(scriptId) && !initiallyLoaded) {
                            script = document.createElement('script');
                            script.setAttribute('src', scriptUrl);
                            script.setAttribute('type', 'text/javascript');
                            script.setAttribute('id', scriptId);
                            document.head.appendChild(script);
                        }
                        return [4 /*yield*/, this.retryUntilValue(loadedCheck, loadWaitMilliseconds)];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns a function that, as long as it continues to be invoked, will not
     * be triggered. The function will be called after it stops being called for
     * N milliseconds. If `immediate` is passed, trigger the function on the
     * leading edge, instead of the trailing.
     * @param immediate - Execute call immediately
     */
    Util.prototype.debounce = function (func, wait, immediate) {
        var timeout;
        return function () {
            var later = function () {
                timeout = null;
                if (!immediate) {
                    func.call.apply(func, tslib_1.__spread([this], arguments));
                }
            };
            var callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later.bind.apply(later, tslib_1.__spread([this], arguments)), wait);
            if (callNow) {
                func.call.apply(func, tslib_1.__spread([this], arguments));
            }
        };
    };
    /**
     * Returns a function that, when invoked, will only be triggered at most once
     * during a given window of time. Normally, the throttled function will run as
     * much as it can, without ever going more than once per `wait` duration.
     */
    Util.prototype.throttle = function (func, wait) {
        var context, args, result;
        var timeout = null;
        var previous = 0;
        var later = function () {
            previous = Date.now();
            timeout = null;
            result = func.apply(context, args);
            if (!timeout) {
                context = args = null;
            }
        };
        return function () {
            var now = Date.now();
            if (!previous) {
                previous = now;
            }
            var remaining = wait - (now - previous);
            context = this;
            args = arguments;
            if (remaining <= 0 || remaining > wait) {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = null;
                }
                previous = now;
                result = func.apply(context, args);
                if (!timeout) {
                    context = args = null;
                }
            }
            else if (!timeout) {
                timeout = setTimeout(later, remaining);
            }
            return result;
        };
    };
    /**
     * Return the middle most item of an array
     */
    Util.prototype.getMiddleItem = function (arr) {
        var retVal = null;
        if (arr && arr.length) {
            retVal = arr[arr.length > 1 ? Math.floor(arr.length / halfDivisor) : 0];
        }
        return retVal;
    };
    /**
     * Get an item from an array, wrapping back to the beginning if the index is greater than the array length
     */
    Util.prototype.getItemWithOverflow = function (arr, index) {
        while (index >= arr.length) {
            index = index - arr.length;
        }
        return arr[index];
    };
    /**
     * Remove items in one array from another
     * @param array Array to remove items from
     * @param remove Array of items to be removed
     */
    Util.prototype.removeFromArray = function (array, remove) {
        return array.filter(function (val) { return !remove.includes(val); });
    };
    /**
     * Returns the given string capitalized
     * @param text String to be capitalized
     */
    Util.prototype.capitalize = function (text) {
        return text[0].toUpperCase() + text.slice(1);
    };
    /**
     * Returns the given source as a new context
     * @param source The object to copy
     */
    Util.prototype.deepCopy = function (source) {
        var _this = this;
        return Array.isArray(source)
            ? source.map(function (item) { return _this.deepCopy(item); })
            : source instanceof Date
                ? new Date(source.getTime())
                : source && typeof source === 'object'
                    ? Object.getOwnPropertyNames(source).reduce(function (o, prop) {
                        Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop));
                        o[prop] = _this.deepCopy(source[prop]);
                        return o;
                    }, Object.create(Object.getPrototypeOf(source)))
                    : source;
    };
    /**
     * Deep clone an object. Exception is thrown if any part of the input value is not serializable.
     * Some ESRI objects have a clone() method. If present, that function is used to perform the copy.
     * @param object The object to deep clone/copy
     */
    Util.prototype.deepClone = function (object) {
        if (typeof object.clone === 'function') {
            return object.clone();
        }
        else {
            return window.structuredClone(object);
        }
    };
    /**
     * Determine if a value is an even or odd number
     * Does not catch if a string isn't a number, just returns false
     */
    Util.prototype.isEven = function (value) {
        var toCheck;
        if (typeof value === 'string') {
            toCheck = parseFloat(value);
            if (isNaN(toCheck)) {
                return false;
            }
        }
        else {
            toCheck = value;
        }
        return toCheck % 2 === 0;
    };
    /**
     * Compares if one string is included in another regardless
     * of capitalization and surrounding whitespace
     */
    Util.prototype.LooseIncludes = function (source, search) {
        return source.toLowerCase().trim().includes(search.toLowerCase().trim());
    };
    return Util;
}());
export var util = new Util();
